import { Group, isSelectEntryEdited } from '@bpmn-io/properties-panel';
import { useCallback, useMemo } from '@bpmn-io/properties-panel/preact/hooks';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import { confirmResetProcessDefinitionSpList } from '../../../../process-definition/confirm-reset-process-definition';
import { resetTasks } from '../../../../process-definition/reset-process-definition';
import type {
    EntryDefinition,
    EntryDefinitionFn,
    FetchState,
    GroupFn,
} from '../../../types/properties-panel';
import type { BpmnProcessSpProps, ProcessSpProps, SpSiteOrList } from '../../../types/sharepoint';
import {
    stepManProps,
    stepManPropsValues,
    stepManPropValue,
} from '../../step-man/step-man-props-util';
import { useFetchSpSiteLists } from '../hooks/useFetchSpSiteLists';
import { useIsGroupOpen } from '../hooks/useIsGroupOpen';
import { useService } from '../hooks/useService';
import { createOption, getOption, getOptionLabel, type Option, SelectEntry } from './SelectEntry';
import { h } from '@bpmn-io/properties-panel/preact';

const groupId = 'spSiteListGroup';

export const ProcessSpGroup: GroupFn = (element, injector) => {
    if (is(element, 'bpmn:Process')) {
        return {
            entries: [SpSiteProps(), SpListProps(), SpStartWhenProps(), SpPrioProps()],
            id: groupId,
            label: injector.get('translate')('SharePoint list'),
            component: Group,
        };
    }
};

type SpGroupComp = EntryDefinition<Partial<BpmnProcessSpProps>>['component'];

const SpSiteProps: EntryDefinitionFn = () => ({
    id: 'spSite',
    component: SpSite,
    isEdited: isSelectEntryEdited,
});

const SpSite: SpGroupComp = (props) => {
    const { element } = props;
    const translate = useService('translate');
    const { siteId, siteName, siteUrl } = stepManPropsValues(
        element,
        'siteId',
        'siteName',
        'siteUrl'
    );

    const options = useMemo(() => [createOption(siteName!, siteId!)], [siteId, siteName, siteUrl]);

    return SelectEntry({
        element,
        disabled: true, // loading,
        // description: loading ? translate('Loading...') : undefined,
        getOptions: useCallback(() => options, [options]),
        getValue: () => siteId || '',
        id: 'spSite',
        label: translate('Site'),
        setValue: (_value) => {},
    });
};

const SpListProps: EntryDefinitionFn = () => ({
    id: 'spList',
    component: SpList,
    isEdited: isSelectEntryEdited,
});

const SpList: SpGroupComp = (props) => {
    const { element } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const { listId, listName, /*listPath,*/ siteUrl } = stepManPropsValues(
        element,
        'listId',
        'listName',
        'siteUrl'
    );
    const { data, loading } = useFetchSpSiteLists({
        enabled: useIsGroupOpen(groupId),
        siteUrl: siteUrl,
    });
    let options = useOptions(data);

    // add missing or default selection
    options = useMemo(() => {
        if (listId) {
            const option = getOption(options, listId);
            return option ? options : [createOption(listName!, listId)].concat(options);
        }

        return [createOption(translate('Choose from'), '', options.length > 0)].concat(options);
    }, [options, listId, listName /*, listPath*/]);

    const getOptions = useCallback(() => options, [options]);

    const tooltipNode = () => {
        return h('div', { className: 'bio-properties-panel-tooltip-content' }, [
            h('span', {}, 'Wählen Sie eine Liste. '),
            h(
                'span',
                {},
                "Falls der Prozess nicht mit einer Liste verbunden ist (- Ohne Zuordnung -) , kann er über 'Prozess starten' in den SharePoint Aktionen gestartet werden."
            ),
        ]);
    };

    return SelectEntry({
        element,
        description: loading ? translate('Loading...') : undefined,
        disabled: loading,
        getOptions,
        getValue: () => listId || '',
        id: 'spList',
        label: translate('List'),
        tooltip: tooltipNode() as any,
        setValue: (value) => {
            if (value === listId) {
                return;
            }
            confirmResetProcessDefinitionSpList(
                (reset) => {
                    if (reset) {
                        resetTasks(element);
                    }

                    modeling.updateProperties(
                        element,
                        stepManProps<ProcessSpProps>({
                            listId: value,
                            listName: getOptionLabel(options, value),
                            // listPath: data?.find(entry => entry.id === value)?.path ?? undefined
                        })
                    );
                },
                () => {
                    const select = document.getElementById('bio-properties-panel-spList');
                    if (select) {
                        (select as HTMLSelectElement).value = listId!;
                        select.dispatchEvent(new Event('input'));
                    }
                },
                listName
            );
        },
    });
};

type FS = FetchState<SpSiteOrList[]>;

const useOptions = (data: FS['data']): Option[] =>
    useMemo(() => (data ? data.map((entry) => createOption(entry.name, entry.id)) : []), [data]);

const SpPrioProps: EntryDefinitionFn = () => ({
    id: 'spPrio',
    component: SpPrio,
    isEdited: isSelectEntryEdited,
});

const SpPrio: SpGroupComp = (props) => {
    const { element } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const options = useMemo(
        () =>
            ['100', '200', '300', '400', '500', '600', '700', '800', '900'].map((value) =>
                createOption(value, value)
            ),
        []
    );

    return SelectEntry({
        element,
        description: translate('Determines the starting order when creating items.'),
        getOptions: useCallback(() => options, [options]),
        getValue: () => (stepManPropValue(element, 'startPriority') || 500).toString(),
        id: 'spPrio',
        label: translate('Start priority'),
        setValue: (value) => {
            modeling.updateProperties(
                element,
                stepManProps<ProcessSpProps>({ startPriority: Number.parseInt(value) })
            );
        },
    });
};

const SpStartWhenProps: EntryDefinitionFn = () => ({
    id: 'spStartWhen',
    component: SpStartWhen,
    isEdited: isSelectEntryEdited,
});

const SpStartWhen: SpGroupComp = (props) => {
    const { element } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const options = useMemo(
        () => [
            createOption('Kein automatischer Start', 'NO_START'),
            createOption('List Item erstellt', 'ITEM_ADDED'),
            createOption('List Item aktualisiert', 'ITEM_UPDATED'),
        ],
        []
    );

    return SelectEntry({
        element,
        description: translate('Determines when the process should be started.'),
        getOptions: useCallback(() => options, [options]),
        getValue: () => (stepManPropValue(element, 'startWhen') || 500).toString(),
        id: 'spPrio',
        label: translate('Start when'),
        setValue: (value) => {
            modeling.updateProperties(element, stepManProps<ProcessSpProps>({ startWhen: value }));
        },
    });
};
